import { ErrorScreen } from './error-screen';
import { setNavHistory } from '@/lib/nav-context';
import { createRootRoute, createRoute, createRouter, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { AskTextHost } from '@/components/ui/ask-text';
import { TopProgress } from '@/components/ui/top-progress';
import { useEffect } from 'react';
import { Toaster } from 'sonner';
import { ChangePasswordPage } from '@/features/auth/change-password';
import { LoginPage } from '@/features/auth/login';
import { ModuleDashboard } from '@/features/dashboard/module-dashboard';
import { ModuleSelection } from '@/features/launcher/module-selection';
import { InboxPage } from '@/features/platform/inbox';
import { ModuleLayout } from '@/features/shell/module-layout';
import { NotFound, SplashScreen } from '@/features/shell/status-pages';
import { ScreenHost } from '@/features/shell/screen-host';
import { useApplyExperience, useExperience } from '@/lib/experience';
import { useLiveConnection } from '@/lib/live';
import { useSession } from '@/lib/session';
import { lazy } from 'react';

const PrintPage = lazy(() => import('@/features/print/print-page').then((m) => ({ default: m.PrintPage })));

export interface ListSearch {
  page?: number;
  size?: number;
  q?: string;
  sort?: string;
  [k: string]: unknown;
}

function Root() {
  useApplyExperience();
  useLiveConnection();
  const theme = useExperience((s) => s.theme);
  return (
    <>
      <TopProgress />
      <Outlet />
      <AskTextHost />
      <Toaster position="top-right" richColors closeButton theme={theme} gap={10} offset={18} visibleToasts={4} toastOptions={{ duration: 4000, classNames: { toast: 'rounded-2xl! shadow-(--shadow-pop)! backdrop-blur-xl! backdrop-saturate-150! border! hover:-translate-y-0.5! transition-transform!', title: 'font-semibold! text-[13.5px]!', description: 'text-[12.5px]! opacity-90!', closeButton: 'transition-transform! hover:scale-110!' } }} />
    </>
  );
}

const rootRoute = createRootRoute({
  component: Root,
  notFoundComponent: NotFound,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof s.redirect === 'string' && s.redirect.startsWith('/') ? s.redirect : undefined,
  }),
  component: LoginPage,
});

const changePasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/change-password', component: ChangePasswordPage });

/** Everything below requires a signed-in user who has no pending password change. */
const authedRoute = createRoute({ getParentRoute: () => rootRoute, id: 'authed', component: AuthedGate });

function AuthedGate() {
  const status = useSession((s) => s.status);
  const mustChange = useSession((s) => s.boot?.user.mustChangePassword);
  const href = useLocation({ select: (l) => l.href });
  const navigate = useNavigate();
  const target = status === 'anonymous' ? 'login' : status === 'authenticated' && mustChange ? 'password' : null;
  useEffect(() => {
    if (target === 'login') void navigate({ to: '/login', search: { redirect: href !== '/' ? href : undefined }, replace: true });
    if (target === 'password') void navigate({ to: '/change-password', replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  if (status !== 'authenticated' || target) return <SplashScreen />;
  return <Outlet />;
}

const launcherRoute = createRoute({ getParentRoute: () => authedRoute, path: '/', component: ModuleSelection });
const inboxRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/inbox',
  validateSearch: (s: Record<string, unknown>): { type?: string; page?: number } => ({
    type: typeof s.type === 'string' ? s.type : undefined,
    page: Number(s.page) > 0 ? Number(s.page) : undefined,
  }),
  component: InboxPage,
});

const printRoute = createRoute({ getParentRoute: () => authedRoute, path: '/print/$kind/$id', component: PrintPage });
const moduleRoute = createRoute({ getParentRoute: () => authedRoute, path: '/m/$module', component: ModuleLayout });
const moduleIndexRoute = createRoute({ getParentRoute: () => moduleRoute, path: '/', component: ModuleDashboard });

const parseListSearch = (s: Record<string, unknown>): ListSearch => {
  const out: ListSearch = { ...s };
  const page = Number(s.page);
  const size = Number(s.size);
  out.page = Number.isInteger(page) && page > 0 ? page : undefined;
  out.size = [10, 25, 50, 100].includes(size) ? size : undefined;
  out.q = typeof s.q === 'string' && s.q ? s.q : undefined;
  out.sort = typeof s.sort === 'string' && s.sort ? s.sort : undefined;
  return out;
};

export const screenRoute = createRoute({
  getParentRoute: () => moduleRoute,
  path: '$screen',
  validateSearch: parseListSearch,
  component: () => <ScreenHost kind="list" />,
});
export const detailRoute = createRoute({
  getParentRoute: () => moduleRoute,
  path: '$screen/$id',
  validateSearch: (s: Record<string, unknown>): { from?: string; mode?: 'view' } => ({ from: typeof s.from === 'string' ? s.from : undefined, mode: s.mode === 'view' ? 'view' : undefined }),
  component: () => <ScreenHost kind="detail" />,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  changePasswordRoute,
  authedRoute.addChildren([launcherRoute, inboxRoute, printRoute, moduleRoute.addChildren([moduleIndexRoute, screenRoute, detailRoute])]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadDelay: 80,
  scrollRestoration: true,
  defaultErrorComponent: ErrorScreen,
});
// Where-did-I-come-from reads the router's position, not the browser's (which is written a moment later).
setNavHistory(router.history);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
